import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';
import { getAllTemplates } from '../../config/templates';

const STEPS = ['Template', 'Details', 'Slug', 'Preview'];

// ---------------------------------------------------------------------------
// Step 1 -- Choose Template
// ---------------------------------------------------------------------------
const StepTemplate = ({ value, onChange }) => {
  const templates = getAllTemplates();

  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-800 mb-1">Choose a Template</h2>
      <p className="text-sm text-slate-500 mb-6">
        Pick a design style for your wedding invitation. You can change this later.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((t) => {
          const selected = value === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => onChange(t.id)}
              className={`relative text-left rounded-xl border-2 p-5 transition ${
                selected
                  ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-200'
                  : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              {/* Color swatch */}
              <div className="flex gap-1.5 mb-3">
                <div
                  className="w-6 h-6 rounded-full border border-white shadow-sm"
                  style={{ backgroundColor: t.theme?.colors?.primary || '#8B7355' }}
                />
                <div
                  className="w-6 h-6 rounded-full border border-white shadow-sm"
                  style={{ backgroundColor: t.theme?.colors?.secondary || '#E8DCC4' }}
                />
                <div
                  className="w-6 h-6 rounded-full border border-white shadow-sm"
                  style={{ backgroundColor: t.theme?.colors?.accent || '#C9A882' }}
                />
              </div>

              <h3 className="text-base font-semibold text-slate-800">{t.name}</h3>
              <p className="text-xs text-slate-500 mt-0.5">{t.category}</p>
              <p className="text-sm text-slate-600 mt-1">{t.description}</p>

              {selected && (
                <div className="absolute top-3 right-3 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 2 -- Couple Details
// ---------------------------------------------------------------------------
const StepDetails = ({ data, onChange }) => {
  const update = (field, val) => onChange({ ...data, [field]: val });

  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-800 mb-1">Couple Details</h2>
      <p className="text-sm text-slate-500 mb-6">Tell us about the happy couple.</p>

      <div className="space-y-4 max-w-md">
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-slate-700">Partner 1</label>
          <input
            type="text"
            value={data.partner1 || ''}
            onChange={(e) => update('partner1', e.target.value)}
            placeholder="First name"
            className="w-full h-10 px-3 rounded-lg border border-slate-300 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-slate-700">Partner 2</label>
          <input
            type="text"
            value={data.partner2 || ''}
            onChange={(e) => update('partner2', e.target.value)}
            placeholder="First name"
            className="w-full h-10 px-3 rounded-lg border border-slate-300 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-slate-700">Wedding Date</label>
          <input
            type="date"
            value={data.date || ''}
            onChange={(e) => update('date', e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 3 -- Choose Slug
// ---------------------------------------------------------------------------
const StepSlug = ({ slug, onChange, autoSlug }) => {
  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-800 mb-1">Choose Your URL</h2>
      <p className="text-sm text-slate-500 mb-6">
        This will be the shareable link for your invitation.
      </p>

      <div className="max-w-md space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500 whitespace-nowrap">ourday.app/w/</span>
          <input
            type="text"
            value={slug}
            onChange={(e) => onChange(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            placeholder={autoSlug || 'your-slug'}
            className="flex-1 h-10 px-3 rounded-lg border border-slate-300 text-sm font-mono placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {autoSlug && slug !== autoSlug && (
          <button
            type="button"
            onClick={() => onChange(autoSlug)}
            className="text-xs text-blue-600 hover:text-blue-700 underline"
          >
            Use suggested: {autoSlug}
          </button>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Step 4 -- Preview & Create
// ---------------------------------------------------------------------------
const StepPreview = ({ template, details, slug, templates }) => {
  const templateInfo = templates.find((t) => t.id === template) || templates[0];

  return (
    <div>
      <h2 className="text-xl font-semibold text-slate-800 mb-1">Review & Create</h2>
      <p className="text-sm text-slate-500 mb-6">Looks good? Let's make it official.</p>

      <div className="max-w-md bg-white rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
        <div className="px-5 py-4">
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Template</p>
          <div className="flex items-center gap-2">
            <div
              className="w-4 h-4 rounded-full"
              style={{ backgroundColor: templateInfo.theme?.colors?.primary }}
            />
            <span className="text-sm font-medium text-slate-700">{templateInfo.name}</span>
          </div>
        </div>

        <div className="px-5 py-4">
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Couple</p>
          <p className="text-sm font-medium text-slate-700">
            {details.partner1 || '---'} & {details.partner2 || '---'}
          </p>
        </div>

        <div className="px-5 py-4">
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">Date</p>
          <p className="text-sm font-medium text-slate-700">
            {details.date
              ? new Date(details.date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })
              : 'Not set'}
          </p>
        </div>

        <div className="px-5 py-4">
          <p className="text-xs text-slate-400 uppercase tracking-wide mb-1">URL</p>
          <p className="text-sm font-mono text-blue-600">ourday.app/w/{slug || '...'}</p>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Onboarding Flow
// ---------------------------------------------------------------------------
const OnboardingFlow = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const templates = getAllTemplates();

  const [step, setStep] = useState(0);
  const [template, setTemplate] = useState(templates[0]?.id || 'timeless');
  const [details, setDetails] = useState({ partner1: '', partner2: '', date: '' });
  const [slug, setSlug] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Auto-generate slug from couple names
  const autoSlug = useMemo(() => {
    const p1 = (details.partner1 || '').trim().toLowerCase().replace(/\s+/g, '');
    const p2 = (details.partner2 || '').trim().toLowerCase().replace(/\s+/g, '');
    if (p1 && p2) return `${p1}-and-${p2}`;
    if (p1) return p1;
    return '';
  }, [details.partner1, details.partner2]);

  // When moving to slug step, pre-fill if empty
  const effectiveSlug = slug || autoSlug;

  const canProceed = () => {
    switch (step) {
      case 0:
        return !!template;
      case 1:
        return !!(details.partner1?.trim() && details.partner2?.trim());
      case 2:
        return !!effectiveSlug;
      case 3:
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (step === 2 && !slug && autoSlug) {
      setSlug(autoSlug);
    }
    if (step < STEPS.length - 1) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleCreate = async () => {
    setSubmitting(true);
    setError('');
    try {
      const finalSlug = slug || autoSlug;
      const coupleName = `${details.partner1.trim()} & ${details.partner2.trim()}`;

      const wedding = await api.createWedding(token, {
        coupleName,
        template,
        slug: finalSlug,
        date: details.date || undefined,
        partner1: details.partner1.trim(),
        partner2: details.partner2.trim(),
      });

      const weddingId = wedding.id || wedding.wedding?.id;
      navigate(`/dashboard/weddings/${weddingId}`, { replace: true });
    } catch (err) {
      setError(err.message || 'Failed to create wedding');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <h1 className="text-lg font-bold text-slate-800">Create Wedding</h1>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-slate-500 hover:text-slate-700 transition"
          >
            Cancel
          </button>
        </div>
      </header>

      {/* Progress bar */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-2">
        <div className="flex items-center gap-2">
          {STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <div className="flex items-center gap-2">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition ${
                    i < step
                      ? 'bg-blue-600 text-white'
                      : i === step
                      ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-300'
                      : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {i < step ? (
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    i + 1
                  )}
                </div>
                <span
                  className={`text-xs font-medium hidden sm:inline ${
                    i <= step ? 'text-slate-700' : 'text-slate-400'
                  }`}
                >
                  {label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-0.5 rounded ${
                    i < step ? 'bg-blue-500' : 'bg-slate-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-6">
            {error}
          </div>
        )}

        {step === 0 && <StepTemplate value={template} onChange={setTemplate} />}
        {step === 1 && <StepDetails data={details} onChange={setDetails} />}
        {step === 2 && <StepSlug slug={slug || autoSlug} onChange={setSlug} autoSlug={autoSlug} />}
        {step === 3 && (
          <StepPreview
            template={template}
            details={details}
            slug={effectiveSlug}
            templates={templates}
          />
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-10">
          <button
            type="button"
            onClick={handleBack}
            disabled={step === 0}
            className="h-10 px-5 text-sm font-medium text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Back
          </button>

          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={!canProceed()}
              className="h-10 px-6 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={handleCreate}
              disabled={submitting || !canProceed()}
              className="h-10 px-6 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[120px]"
            >
              {submitting ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                'Create Wedding'
              )}
            </button>
          )}
        </div>
      </main>
    </div>
  );
};

export default OnboardingFlow;
